import React from 'react';
import { get, uniqueId, isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';
import { IntlShape, injectIntl } from 'react-intl';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
} from '@material-ui/core';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { StyledDiv } from 'containers/CourtManagerLegalProceedings/ExpertiseExpert/Dialog/styles';
import DecisionDialog from 'components/DecisionDialog/index';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/ExpertiseExpert/messages';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import {
  ASSIGNEMENT_EXPERTISE_TYPE,
  EXPERTISE_ACCOUNTING_ID,
} from 'containers/CourtManagerLegalProceedings/constants';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { EXPERTISE_MECHANIC_ID } from 'containers/CourtManagerLegalProceedings/constants';
import { returnDefaultCodeByType } from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';

export interface AppreciationDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  validateDialog: Function;
  recap: any;
  listings: any;
  corporealExperts: any;
  claimNumber: string;
  transactionId?: any;
  timelineId?: any;
}

const AppreciationDialog: React.FC<AppreciationDialogProps> = (
  props,
): JSX.Element => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const { onClose, open } = props;
  const [assignedExperts, setAssignedExperts] = React.useState<string[]>([]);

  /* istanbul ignore next */
  const handleChangeSelect = (event: any) => {
    setAssignedExperts(event.target.value);
  };
  /* istanbul ignore next */
  const successCallBack = () => {
    toast.success(translate(messages.toastValidationAppreciation, props));
    props.onClose();
  };

  /* istanbul ignore next */
  const handleValidate = () => {
    props.validateDialog(
      {
        id: get(props.recap, 'id'),
        experts: assignedExperts.map((element: any) => ({
          type: ASSIGNEMENT_EXPERTISE_TYPE.APPRECIATION,
          missionId: get(props.recap, 'missionId'),
          expertCode: element,
        })),
        intervenantSagis: assignedExperts.map((element: any) => ({
          intervenantTypeCode: returnDefaultCodeByType(
            Number(get(props.recap, 'mission.type', '')),
          ),
          intervenantCode: element,
        })),
        assignementDetails: {
          contractorFees: assignedExperts.map((element: any) => ({
            uniqueId: uniqueId(),
            contractorTypeCode: returnDefaultCodeByType(
              Number(get(props.recap, 'mission.type', '')),
            ),
            contractorCode: element,
            amount: 0,
            isPayment: false,
          })),
          uuid: uuidV4(),
        },
        isVehicle:
          Number(get(props.recap, 'mission.type', '')) ===
            EXPERTISE_MECHANIC_ID ||
          (Number(get(props.recap, 'mission.type', '')) ===
            EXPERTISE_ACCOUNTING_ID &&
            !isEmpty(get(props.recap, 'mission.missionVehicle', [])))
            ? true
            : false,
        claimId: get(props.recap, 'claimId'),
        missionId: get(props.recap, 'missionId'),
        claimNumber: props.claimNumber,
        decidedBy: currentUserStorage.get().username,
        isInboxesActive: featureService.hasFeature(
          FEATURES.ATTRIBUTION_INBOXES_MANAGER,
        ),
        transactionId: props.transactionId,
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        timelineId: props.timelineId,
        typeExpertise: Number(get(props.recap, 'mission.type', '')),
      },
      successCallBack,
    );
  };
  const assignedExpertMission = get(
    props.recap,
    'mission.assignedExperts[0].expertCode',
    '',
  );
  /* istanbul ignore next */
  const allMedicalExperts =
    props.corporealExperts &&
    props.corporealExperts.filter(
      (element: { attributes: { sagis_code: any } }) =>
        /* istanbul ignore next */ !isEmpty(element?.attributes?.sagis_code) &&
        element?.attributes?.sagis_code != assignedExpertMission,
    );
  return (
    <DecisionDialog
      open={open}
      onClose={onClose}
      validateDialog={handleValidate}
      title={translate({ ...messages.appreciationTitle }, props)}
      content={
        <StyledDiv>
          <FormControl className="input">
            <InputLabel ref={inputLabel} id="idd-label-id">
              {translate(messages.expertConseilField, props)}
            </InputLabel>
            <Select
              labelId="idd-label-id"
              name={'assignedExpert'}
              value={assignedExperts}
              renderValue={
                /* istanbul ignore next */ (assignedExperts: any) =>
                  /* istanbul ignore next */ ((assignedExperts as any).map(
                    (element: any) =>
                      getValueByCode(
                        element as string,
                        get(props.listings, 'intervenants'),
                      ),
                  ) as any).join(', ') as any
              }
              onChange={handleChangeSelect}
              labelWidth={60}
              multiple
            >
              {/* istanbul ignore next */ !isEmpty(allMedicalExperts) &&
                allMedicalExperts.map((
                  /* istanbul ignore next */ val: {
                    attributes: { sagis_code: [] };
                    firstName: string;
                    lastName: string;
                  },
                ) => (
                  /* istanbul ignore next */
                  <MenuItem
                    key={uniqueId('stackholder_')}
                    value={
                      get(val.attributes.sagis_code, '[0]')
                        ? get(val.attributes.sagis_code, '[0]')
                        : null
                    }
                  >
                    <Checkbox
                      checked={
                        assignedExperts.indexOf(
                          get(val.attributes.sagis_code, '[0]'),
                        ) > -1
                      }
                    />
                    {val.firstName + ' ' + val.lastName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </StyledDiv>
      }
      disabled={isEmpty(assignedExperts)}
      primaryButtonTitle={translate(messages.dialogValidationButton, props)}
      scondaryButtonTitle={translate(messages.dialogCancelButton, props)}
    />
  );
};

AppreciationDialog.defaultProps = {
  open: false,
};

export default injectIntl(AppreciationDialog);
